import React, { useEffect, useRef } from "react";

import { faWarning } from "@fortawesome/pro-solid-svg-icons";
import { twJoin } from "tailwind-merge";

import { CellTypeSelector } from "src/cases/caseView/blocks/CellTypeSelector";
import {
  CellDisplayType,
  extractCurrencyAmount,
  isExplicitActivationCell,
  isStringCell,
  isValueCompatible,
  MIN_STRING_CELL_HEIGHT,
  resolveCellDisplayType,
  resolveEffectiveType,
} from "src/cases/caseView/blocks/cellTypes";
import {
  ScrollableStringCell,
  TypedCellDisplay,
} from "src/cases/caseView/blocks/TypedCellDisplay";
import { TypedCellEditor } from "src/cases/caseView/blocks/TypedCellEditor";
import {
  useIsCellEditing,
  useIsCellSelected,
  useSelectionActions,
} from "src/cases/caseView/blocks/useEditableTableSelection";
import { useEditorFloor } from "src/cases/caseView/blocks/useEditorFloor";
import { useVerticalCellResize } from "src/cases/caseView/blocks/useVerticalCellResize";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";

export const CellResizeHandle: React.FC<{
  isResizing: boolean;
  onResizeStart: (e: React.MouseEvent) => void;
}> = ({ isResizing, onResizeStart }) => (
  <button
    className="group/cell-resize absolute inset-x-0 bottom-0 z-10 flex h-2 cursor-s-resize items-end justify-center"
    data-loc="cell-resize-handle"
    type="button"
    onMouseDown={onResizeStart}
  >
    <span
      className={twJoin(
        "block h-0.5 w-6 rounded-full bg-indigo-500 transition-opacity group-hover/cell-resize:opacity-100 group-hover/table-cell:opacity-100",
        isResizing ? "opacity-100" : "opacity-0",
      )}
    />
  </button>
);

type EditableTableCellProps = {
  value: unknown;
  columnKey: string;
  rowIndex: number;
  isEditable: boolean;
  isEdited: boolean;
  displayType: CellDisplayType;
  hasDeclaredType: boolean;
  columnValues?: unknown[];
  onChange: (newValue: unknown) => void;
  typeOverride?: CellDisplayType;
  onTypeOverride?: (newType: CellDisplayType | null) => void;
  /** Height the user dragged this cell to. Owned by the parent so it can
   *  outlive edit mode and render in the read-only view too. */
  userHeight?: number;
  onResize?: (height: number) => void;
};

export const EditableTableCell: React.FC<EditableTableCellProps> = ({
  value,
  columnKey,
  rowIndex,
  isEditable,
  isEdited,
  displayType,
  hasDeclaredType,
  columnValues,
  onChange,
  typeOverride,
  onTypeOverride,
  userHeight,
  onResize,
}) => {
  const isSelected = useIsCellSelected(rowIndex, columnKey);
  const isEditing = useIsCellEditing(rowIndex, columnKey);
  const { selectCell, deselectCell, exitEditing, setEditing } =
    useSelectionActions();

  const cellRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isSelected) {
      cellRef.current?.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [isSelected]);

  const baseDisplayTypeRef = useRef(displayType);
  if (!typeOverride) {
    baseDisplayTypeRef.current = displayType;
  }
  const baseDisplayType = baseDisplayTypeRef.current;

  const effectiveDisplayType = resolveEffectiveType(
    value,
    resolveCellDisplayType(value, displayType, typeOverride),
  );

  const handleTypeOverride = onTypeOverride
    ? (newType: CellDisplayType) => {
        // Type-only on purpose: on a declared-currency column a new symbol
        // needs no override (it lives on the value), while elsewhere the
        // override is what makes the cell a currency at all.
        const isDeclaredType = newType.type === baseDisplayType.type;
        onTypeOverride(isDeclaredType ? null : newType);

        // Currency holds { amount, symbol }: switching in wraps the value and
        // switching to a number unwraps it, so the amount round-trips.
        if (newType.type === "currency") {
          onChange({
            amount: extractCurrencyAmount(value),
            symbol: newType.symbol,
          });
          return;
        }
        if (
          newType.type === "number" &&
          effectiveDisplayType.type === "currency"
        ) {
          onChange(extractCurrencyAmount(value));
          return;
        }

        // Any other type reads the value differently, so it starts empty.
        onChange(null);
        if (!isDeclaredType && isExplicitActivationCell(newType.type)) {
          setEditing();
        }
      }
    : undefined;

  const hasValueMismatch =
    isEditable &&
    value !== null &&
    value !== undefined &&
    !isValueCompatible(value, displayType);
  const hasActiveTypeOverride =
    isEditable && typeOverride != null && hasDeclaredType;
  const showWarningIcon = hasValueMismatch || hasActiveTypeOverride;

  const showOrangeBorder = isSelected && showWarningIcon;

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isEditable) return;
    if (!isSelected) {
      e.preventDefault();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      selectCell(rowIndex, columnKey);
    } else if (
      isExplicitActivationCell(effectiveDisplayType.type) &&
      !isEditing
    ) {
      e.preventDefault();
    }
  };

  const handleClick = () => {
    if (!isEditable || !isSelected) return;
    if (isExplicitActivationCell(effectiveDisplayType.type) && !isEditing) {
      setEditing();
    }
  };

  const handleBlur = isExplicitActivationCell(effectiveDisplayType.type)
    ? () => exitEditing(rowIndex, columnKey)
    : deselectCell;

  const showEditor =
    isEditable &&
    (isExplicitActivationCell(effectiveDisplayType.type)
      ? isEditing
      : isSelected);

  const stringCell = isStringCell(value, effectiveDisplayType);

  const { editorFloor, setEditorFloor } = useEditorFloor(
    showEditor,
    userHeight,
  );

  const handleDragResize = (height: number) => {
    setEditorFloor(height);
    onResize?.(height);
  };

  const { isResizing, handleResizeStart } = useVerticalCellResize({
    cellRef,
    onHeightChange: handleDragResize,
  });

  // One height state for both modes: the stored height, or a single line.
  // The open editor sizes freely above the floor; drags, typing, and the
  // closing trim all write through onResize.
  const hasStoredHeight = userHeight !== undefined;
  const stringCellStyle = showEditor
    ? { minHeight: editorFloor ?? MIN_STRING_CELL_HEIGHT }
    : { minHeight: MIN_STRING_CELL_HEIGHT, height: userHeight };

  return (
    <>
      {/* Separate layer: string cells auto-size, so the content div below
          doesn't always fill the <td>. It carries the same handlers so the
          gap below a short cell behaves as part of it. */}
      <div
        className={twJoin(
          "absolute inset-0",
          isEditable ? "cursor-text" : "cursor-default",
          !isEditable && "bg-gray-50",
          isSelected &&
            isEditable &&
            (showOrangeBorder
              ? "bg-orange-50 ring-1 ring-orange-400 ring-inset"
              : "bg-indigo-50 ring-1 ring-indigo-500 ring-inset"),
          !isSelected && isEdited && "bg-yellow-50",
        )}
        data-loc={`editable-table-cell-highlight-${rowIndex}-${columnKey}`}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
      />
      <div
        ref={cellRef}
        className={twJoin(
          "relative flex w-full contain-inline-size",
          stringCell && (showEditor || hasStoredHeight)
            ? "items-start"
            : "h-8 items-center",
          "font-inter-normal-12px text-gray-800",
          isEditable ? "cursor-text" : "cursor-default text-gray-500",
        )}
        data-loc={`editable-table-cell-${rowIndex}-${columnKey}`}
        style={stringCell ? stringCellStyle : undefined}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
      >
        {showWarningIcon && (
          <div
            className="absolute top-1/2 left-1 z-10 -translate-y-1/2"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Tooltip
              body={
                hasActiveTypeOverride
                  ? `Type changed from "${baseDisplayType.type}" to "${effectiveDisplayType.type}"`
                  : `Value does not match column type "${displayType.type}"`
              }
              isInteractive={false}
              placement="top"
              title={hasActiveTypeOverride ? "Type override" : "Type mismatch"}
            >
              <Icon
                dataLoc="cell-type-mismatch-warning"
                icon={faWarning}
                size="2xs"
                variant="warning"
              />
            </Tooltip>
          </div>
        )}

        {showEditor ? (
          <TypedCellEditor
            key={effectiveDisplayType.type}
            columnKey={columnKey}
            columnValues={columnValues}
            displayType={effectiveDisplayType}
            hasLeftIcon={showWarningIcon}
            floorHeight={editorFloor}
            value={value}
            onBlur={handleBlur}
            onChange={onChange}
            onContentHeightChange={onResize}
          />
        ) : stringCell && hasStoredHeight ? (
          <ScrollableStringCell
            className={showWarningIcon ? "pr-2 pl-6" : "pr-2 pl-1.5"}
            columnKey={columnKey}
            displayType={effectiveDisplayType}
            height={userHeight}
            value={value}
            disableHover
          />
        ) : (
          <div
            className={twJoin(
              // h-full clips tall content (e.g. markdown) to the 32px
              // cell, like the read-only table does.
              "flex h-full min-w-0 flex-1 overflow-hidden whitespace-nowrap py-1.5 pr-2",
              showWarningIcon ? "pl-6" : "pl-1.5",
            )}
          >
            <TypedCellDisplay
              columnKey={columnKey}
              displayType={effectiveDisplayType}
              value={value}
              disableHover
            />
          </div>
        )}
      </div>

      {/* Outside the content div so it sits at the bottom of the full <td>.
          Non-editable cells can't be selected, so their handle always renders. */}
      {stringCell && (isSelected || !isEditable) && (
        <CellResizeHandle
          isResizing={isResizing}
          onResizeStart={handleResizeStart}
        />
      )}

      {isSelected && isEditable && handleTypeOverride && (
        <div
          className="absolute -top-4 right-1 z-20"
          onClick={(e) => e.stopPropagation()}
          onPointerDownCapture={(e) => e.preventDefault()}
        >
          <CellTypeSelector
            currentType={effectiveDisplayType}
            declaredType={baseDisplayType}
            onTypeChange={handleTypeOverride}
          />
        </div>
      )}
    </>
  );
};
